/**
 * TC-UI — Role-specific ticket creation form.
 * Variants: 'parent' | 'student' | 'staff'
 *  - Parent  : Parent name, relationship, student name, GR, grade + common fields
 *  - Student : Student name, GR, grade + common fields
 *  - Staff   : Staff name + common fields
 * Common   : Email (auto), Country code + Contact, Main Dept, Sub Dept, Location,
 *            Description, Photo (camera + gallery, 10MB, optional), Submit
 * Urgency level is intentionally removed per product spec.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { ticketAPI, configAPI, authAPI, generateIdempotencyKey } from '@/lib/api';
import { X, ShieldAlert, CheckCircle, Camera, Image as ImageIcon, Clock } from 'lucide-react';

const REQUIRED_DOMAIN = 'mitgurukul.com';

const FIELDS_BY_VARIANT = {
  parent:  ['parent_name', 'relationship', 'student_name', 'gr_number', 'grade'],
  student: ['student_name', 'gr_number', 'grade'],
  staff:   [],
};

const LABELS = {
  parent_name:  { label: 'Parent Full Name *',           placeholder: 'Enter parent full name' },
  relationship: { label: 'Relationship with Child *',    placeholder: 'Select relationship' },
  student_name: { label: 'Student Full Name *',          placeholder: 'Enter student full name' },
  gr_number:    { label: 'GR Number *',                  placeholder: 'Enter GR number' },
  grade:        { label: 'Grade *',                      placeholder: 'Select grade' },
};

/**
 * Email domain validation policy:
 *  - Staff / Admin / Creator / Resolver → must use @mitgurukul.com (official school ID)
 *  - Parent / Student → any valid email is accepted (personal Gmail/Outlook etc.)
 * This matches the landing-page notice: "Staff must login with your VGWS school official mail ID".
 */
const validateEmailDomain = (email, variant) => {
  if (!email || !email.includes('@')) return { valid: false, error: 'Invalid email format' };
  if (variant === 'parent' || variant === 'student') {
    // Non-staff roles are allowed any email provider
    return { valid: true, error: null };
  }
  const domain = email.toLowerCase().split('@')[1];
  if (domain !== REQUIRED_DOMAIN) {
    return {
      valid: false,
      error: `Access Denied: Please use your official school email ID (@${REQUIRED_DOMAIN}) to submit tickets.`,
    };
  }
  return { valid: true, error: null };
};

const inferVariant = (user) => {
  try {
    // Keep in sync with Landing.js — we persist 'vgws_intended_role' when the
    // user chose a role card before logging in. This is a non-sensitive UI
    // preference (the role key only, not any token). The real session cookie
    // is httpOnly and set by the backend at /api/auth/session; RBAC on the
    // backend is the source of truth — intended_role is purely a UI hint.
    const intent = (localStorage.getItem('vgws_intended_role') || '').toLowerCase();
    if (intent === 'parent') return 'parent';
    if (intent === 'student') return 'student';
    if (intent === 'creator' || intent === 'resolver' || intent === 'admin') return 'staff';
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      console.debug('CreateTicketDialog: localStorage read failed:', error?.message);
    }
  }
  const role = (user?.role || '').toLowerCase();
  if (role === 'student') return 'student';
  if (role === 'parent') return 'parent';
  return 'staff';
};

const CreateTicketDialog = ({ open, onClose, onSuccess, user }) => {
  const variant = useMemo(() => inferVariant(user), [user]);
  const roleFields = FIELDS_BY_VARIANT[variant] || [];

  const [form, setForm] = useState({
    requester_name: '',
    requester_email: '',
    country_code: '+91',
    contact: '',
    main_department: '',
    sub_department: '',
    location: '',
    description: '',
    // role-specific
    parent_name: '',
    relationship: '',
    student_name: '',
    gr_number: '',
    grade: '',
  });
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [emailValid, setEmailValid] = useState({ checked: false, valid: false, error: null });

  const [cfg, setCfg] = useState({
    grades: [], country_codes: [], relationships: [],
    main_departments: [], sub_departments: {}, locations: [],
  });

  const galleryInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const prevOpenRef = useRef(false);

  // Fetch form config + reset on open (silent-retry, no error toast)
  useEffect(() => {
    const justOpened = open && !prevOpenRef.current;
    prevOpenRef.current = open;
    if (!justOpened) return;

    let cancelled = false;
    const loadConfig = async (attempt = 0) => {
      try {
        const res = await configAPI.getTicketFormConfig();
        if (!cancelled) setCfg(res.data);
      } catch (err) {
        if (cancelled) return;
        // Silent retry up to 3 times with back-off — avoids spurious
        // "Failed to load data" toasts during transient network hiccups.
        if (attempt < 3) {
          setTimeout(() => loadConfig(attempt + 1), 800 * (attempt + 1));
        } else if (process.env.NODE_ENV !== 'production') {
          console.debug('CreateTicketDialog: config load failed after retries:', err?.message);
        }
      }
    };
    loadConfig();

    const userName = user?.name || '';
    setForm({
      requester_name: userName,
      requester_email: user?.email || '',
      // Auto-prefill country_code & contact from the user's saved profile so
      // returning users don't need to retype their phone number every time.
      country_code: user?.country_code || '+91',
      contact: user?.contact || '',
      main_department: '',
      sub_department: '',
      location: '',
      description: '',
      parent_name: '',
      relationship: '',
      student_name: variant === 'student' ? userName : '',
      gr_number: '',
      grade: '',
    });
    setPhoto(null);
    setPhotoPreview(null);

    // Refresh the user's saved contact from the backend whenever the dialog
    // opens. The parent component's `user` prop may be stale (loaded once at
    // mount, not refetched between submissions), so a fresh call ensures the
    // auto-prefill always reflects the LAST contact this user submitted with
    // — even within the same session.
    (async () => {
      try {
        const me = await authAPI.getMe();
        if (cancelled) return;
        const saved = me?.data?.contact;
        const savedCC = me?.data?.country_code;
        if (saved || savedCC) {
          setForm((prev) => ({
            ...prev,
            country_code: savedCC || prev.country_code,
            contact: saved || prev.contact,
          }));
        }
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
          console.debug('CreateTicketDialog: contact prefill refetch failed', err?.message);
        }
      }
    })();

    if (user?.email) {
      const v = validateEmailDomain(user.email, variant);
      setEmailValid({ checked: true, valid: v.valid, error: v.error });
    }

    return () => { cancelled = true; };
  }, [open, user, variant]);

  const subDepartmentOptions = form.main_department
    ? (cfg.sub_departments?.[form.main_department] || [])
    : [];

  const handleField = (key, value) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'main_department') next.sub_department = '';
      return next;
    });
  };

  const handleContactChange = (e) => {
    const cleaned = e.target.value.replace(/[^\d]/g, '');
    setForm((p) => ({ ...p, contact: cleaned }));
  };

  const handleFilePicked = useCallback((file) => {
    if (!file) return;
    if (!file.type?.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10 MB');
      return;
    }
    setPhoto(file);
    const reader = new FileReader();
    reader.onload = () => setPhotoPreview(reader.result);
    reader.readAsDataURL(file);
  }, []);

  const removePhoto = () => {
    setPhoto(null);
    setPhotoPreview(null);
    if (galleryInputRef.current) galleryInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  const validateBeforeSubmit = () => {
    if (!emailValid.valid) {
      toast.error(emailValid.error || `Access Denied: Only @${REQUIRED_DOMAIN} emails are allowed.`);
      return false;
    }
    for (const key of roleFields) {
      if (!form[key] || !String(form[key]).trim()) {
        toast.error(`Please fill: ${LABELS[key].label.replace(' *', '')}`);
        return false;
      }
    }
    if (!form.contact.trim()) { toast.error('Please enter your contact number'); return false; }
    if (!form.main_department) { toast.error('Please select Main Ticket Resolving Department'); return false; }
    if (!form.sub_department) { toast.error('Please select Sub Ticket Resolving Department'); return false; }
    if (!form.location) { toast.error('Please select Location of the Problem'); return false; }
    if (!form.description.trim()) { toast.error('Please describe the problem in detail'); return false; }
    if (form.description.trim().length < 10) { toast.error('Description must be at least 10 characters'); return false; }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateBeforeSubmit()) return;

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('requester_name',
        variant === 'parent' ? form.parent_name
        : variant === 'student' ? form.student_name
        : form.requester_name);
      fd.append('requester_email', form.requester_email);
      fd.append('requester_role',
        variant === 'parent' ? 'parent'
        : variant === 'student' ? 'student'
        : 'staff');
      fd.append('contact', form.contact);
      // Legacy 'department' field: send the selected location for backward compat
      fd.append('department', form.location);
      fd.append('description', form.description);
      fd.append('urgency', 'Medium');  // default; UI removed per spec
      fd.append('country_code', form.country_code);
      fd.append('main_department', form.main_department);
      fd.append('sub_department', form.sub_department);
      fd.append('location', form.location);
      if (variant === 'parent') {
        fd.append('parent_name', form.parent_name);
        fd.append('relationship', form.relationship);
        fd.append('student_name', form.student_name);
        fd.append('gr_number', form.gr_number);
        fd.append('grade', form.grade);
      } else if (variant === 'student') {
        fd.append('student_name', form.student_name);
        fd.append('gr_number', form.gr_number);
        fd.append('grade', form.grade);
      }
      fd.append('idempotency_key', generateIdempotencyKey());
      if (photo) fd.append('photo', photo);

      await ticketAPI.createTicket(fd);
      toast.success('Ticket submitted successfully. Notification email has been sent.');
      setTimeout(() => onSuccess(), 1500);
    } catch (err) {
      const detail = err?.response?.data?.detail || '';
      toast.error(detail || 'Failed to create ticket. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const title = variant === 'parent' ? 'Create Ticket — Parent'
              : variant === 'student' ? 'Create Ticket — Student'
              : 'Create Ticket — Staff';

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto mx-2 sm:mx-auto p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle data-testid="ticket-dialog-title" className="font-heading text-xl sm:text-2xl">
            {title}
          </DialogTitle>
          <DialogDescription className="text-xs sm:text-sm">
            Submit your concern. Our team will respond within the defined SLA timeframe.
          </DialogDescription>
        </DialogHeader>

        {emailValid.checked && !emailValid.valid && (
          <div className="bg-red-100 border-2 border-red-600 rounded-lg p-4 my-2" data-testid="email-validation-error">
            <div className="flex items-start gap-3">
              <ShieldAlert className="w-6 h-6 text-red-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="text-red-800 font-bold text-sm">ACCESS DENIED</h4>
                <p className="text-red-700 text-sm mt-1">{emailValid.error}</p>
                <p className="text-red-600 text-xs mt-2">
                  Your email: <span className="font-mono bg-red-200 px-1 rounded">{user?.email}</span>
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Working hours notice — shown on all variants (Parent, Student, Staff, Admin) */}
        <div
          className="bg-amber-50 border-l-4 border-amber-500 rounded-md p-3 sm:p-4 my-2"
          data-testid="working-hours-notice"
          role="note"
          aria-label="VGWS working hours and ticket processing policy"
        >
          <div className="flex items-start gap-3">
            <Clock className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
            <div className="flex-1">
              <h4 className="text-amber-900 font-semibold text-sm mb-1">
                Please Note — VGWS Working Timeline
              </h4>
              <ul className="text-amber-900 text-xs sm:text-sm space-y-0.5 list-disc list-inside marker:text-amber-600">
                <li>VGWS Staff working hours: <span className="font-semibold">8:00 AM – 6:00 PM</span></li>
                <li>Working days: <span className="font-semibold">Monday to Friday</span></li>
                <li><span className="font-semibold">Sunday</span> is a non-working day</li>
                <li>
                  Tickets submitted on non-working days, or outside working hours will be
                  <span className="font-semibold"> processed on the next working day</span>.
                </li>
              </ul>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 pt-2" data-testid={`ticket-form-${variant}`}>
          {/* Parent-specific */}
          {variant === 'parent' && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="parent_name">{LABELS.parent_name.label}</Label>
                  <Input
                    id="parent_name" data-testid="parent-name"
                    value={form.parent_name}
                    onChange={(e) => handleField('parent_name', e.target.value)}
                    placeholder={LABELS.parent_name.placeholder}
                  />
                </div>
                <div>
                  <Label htmlFor="relationship">{LABELS.relationship.label}</Label>
                  <Select value={form.relationship} onValueChange={(v) => handleField('relationship', v)}>
                    <SelectTrigger id="relationship" data-testid="relationship-select">
                      <SelectValue placeholder={LABELS.relationship.placeholder} />
                    </SelectTrigger>
                    <SelectContent>
                      {cfg.relationships.map((r) => (
                        <SelectItem key={r} value={r} data-testid={`relationship-option-${r.toLowerCase()}`}>{r}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </>
          )}

          {/* Student-name field (shown for parent + student variants) */}
          {(variant === 'parent' || variant === 'student') && (
            <div>
              <Label htmlFor="student_name">{LABELS.student_name.label}</Label>
              <Input
                id="student_name" data-testid="student-name"
                value={form.student_name}
                onChange={(e) => handleField('student_name', e.target.value)}
                placeholder={LABELS.student_name.placeholder}
              />
            </div>
          )}

          {/* GR + Grade row */}
          {(variant === 'parent' || variant === 'student') && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="gr_number">{LABELS.gr_number.label}</Label>
                <Input
                  id="gr_number" data-testid="gr-number"
                  value={form.gr_number}
                  onChange={(e) => handleField('gr_number', e.target.value)}
                  placeholder={LABELS.gr_number.placeholder}
                />
              </div>
              <div>
                <Label htmlFor="grade">{LABELS.grade.label}</Label>
                <Select value={form.grade} onValueChange={(v) => handleField('grade', v)}>
                  <SelectTrigger id="grade" data-testid="grade-select">
                    <SelectValue placeholder={LABELS.grade.placeholder} />
                  </SelectTrigger>
                  <SelectContent>
                    {cfg.grades.map((g) => (
                      <SelectItem key={g} value={g} data-testid={`grade-option-${g}`}>{g}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Staff name (staff variant only — Name + Email row) */}
          {variant === 'staff' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="requester_name">Your Full Name *</Label>
                <Input
                  id="requester_name" data-testid="requester-name"
                  value={form.requester_name}
                  onChange={(e) => handleField('requester_name', e.target.value)}
                  placeholder="Enter your full name"
                />
              </div>
              <div>
                <Label htmlFor="requester_email">Email ID *</Label>
                <Input
                  id="requester_email" data-testid="requester-email"
                  type="email" value={form.requester_email} readOnly disabled
                  className="bg-slate-50"
                />
              </div>
            </div>
          )}

          {/* Email row for parent/student variants */}
          {(variant === 'parent' || variant === 'student') && (
            <div>
              <Label htmlFor="requester_email">Email ID *</Label>
              <Input
                id="requester_email" data-testid="requester-email"
                type="email" value={form.requester_email} readOnly disabled
                className="bg-slate-50"
              />
            </div>
          )}

          {/* Contact row: country code + phone */}
          <div>
            <Label>Contact Number *</Label>
            <div className="flex gap-2">
              <div className="w-40">
                <Select value={form.country_code} onValueChange={(v) => handleField('country_code', v)}>
                  <SelectTrigger data-testid="country-code-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {cfg.country_codes.map((c) => (
                      <SelectItem key={c.code} value={c.code} data-testid={`country-option-${c.code}`}>
                        {c.code} · {c.country}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Input
                data-testid="contact-input"
                type="tel" inputMode="numeric" placeholder="Enter phone number"
                value={form.contact}
                onChange={handleContactChange}
                className="font-mono flex-1"
              />
            </div>
          </div>

          {/* Main + Sub Department */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Main Ticket Resolving Department *</Label>
              <Select value={form.main_department} onValueChange={(v) => handleField('main_department', v)}>
                <SelectTrigger data-testid="main-dept-select">
                  <SelectValue placeholder="Select main department" />
                </SelectTrigger>
                <SelectContent>
                  {cfg.main_departments.map((m) => (
                    <SelectItem key={m} value={m} data-testid={`main-dept-option-${m}`}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Sub Ticket Resolving Department *</Label>
              <Select
                value={form.sub_department}
                onValueChange={(v) => handleField('sub_department', v)}
                disabled={!form.main_department}
              >
                <SelectTrigger data-testid="sub-dept-select">
                  <SelectValue placeholder={form.main_department ? 'Select sub department' : 'Select main first'} />
                </SelectTrigger>
                <SelectContent>
                  {subDepartmentOptions.map((s) => (
                    <SelectItem key={s} value={s} data-testid={`sub-dept-option-${s}`}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Problem Location */}
          <div>
            <Label>Location of the Problem *</Label>
            <Select value={form.location} onValueChange={(v) => handleField('location', v)}>
              <SelectTrigger data-testid="location-select">
                <SelectValue placeholder="Select problem location" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {cfg.locations.map((loc) => (
                  <SelectItem key={loc} value={loc} data-testid={`location-option-${loc}`}>{loc}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Description */}
          <div>
            <Label htmlFor="description">Describe the Problem in Detail *</Label>
            <Textarea
              id="description" data-testid="description-input"
              rows={4} className="resize-none"
              placeholder="Please describe the issue in detail (minimum 10 characters)..."
              value={form.description}
              onChange={(e) => handleField('description', e.target.value)}
            />
            <p className="text-xs text-slate-500 mt-1">
              {form.description.length} characters
              {form.description.length > 0 && form.description.length < 10 && (
                <span className="text-red-500 ml-2">(minimum 10 required)</span>
              )}
            </p>
          </div>

          {/* Photo upload: Camera + Gallery. 10MB. Optional. 24h auto-purge notice. */}
          <div>
            <Label>Upload Photo (Optional)</Label>
            <p className="text-xs text-slate-500 mb-2">
              Up to 10 MB. Photos are permanently purged 24 hours after submission.
            </p>
            {!photoPreview ? (
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  data-testid="photo-camera-btn"
                  onClick={() => cameraInputRef.current?.click()}
                  className="border-2 border-dashed border-slate-300 hover:border-[#E97832] rounded-lg p-5 text-center transition-colors flex flex-col items-center gap-2"
                >
                  <Camera className="w-7 h-7 text-slate-500" />
                  <span className="text-sm font-medium text-slate-700">Take Photo</span>
                  <span className="text-xs text-slate-500">Uses camera</span>
                </button>
                <button
                  type="button"
                  data-testid="photo-gallery-btn"
                  onClick={() => galleryInputRef.current?.click()}
                  className="border-2 border-dashed border-slate-300 hover:border-[#E97832] rounded-lg p-5 text-center transition-colors flex flex-col items-center gap-2"
                >
                  <ImageIcon className="w-7 h-7 text-slate-500" />
                  <span className="text-sm font-medium text-slate-700">Choose From Gallery</span>
                  <span className="text-xs text-slate-500">PNG / JPG · 10 MB max</span>
                </button>
              </div>
            ) : (
              <div className="relative">
                <img src={photoPreview} alt="Preview" className="w-full h-48 object-cover rounded-lg border" />
                <Button
                  type="button" size="sm" variant="destructive"
                  onClick={removePhoto} className="absolute top-2 right-2"
                  data-testid="photo-remove-btn"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            )}
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => handleFilePicked(e.target.files?.[0])}
              data-testid="photo-camera-input"
            />
            <input
              ref={galleryInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => handleFilePicked(e.target.files?.[0])}
              data-testid="photo-gallery-input"
            />
          </div>

          {/* Submit */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid="submit-ticket-btn"
              disabled={submitting || !emailValid.valid}
              className={`min-w-[160px] ${
                emailValid.valid
                  ? 'bg-gradient-to-r from-[#E97832] to-[#EA580C] hover:from-[#C25E20] hover:to-[#C2410C]'
                  : 'bg-gray-400 cursor-not-allowed'
              }`}
            >
              {submitting ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  Submitting...
                </span>
              ) : !emailValid.valid ? (
                'Email Not Authorized'
              ) : (
                <span className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" /> Submit Ticket
                </span>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateTicketDialog;
